import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Alert, Button, Divider, Input, message, Modal, Spin, Tag } from 'antd';
import { connect, useDispatch } from 'react-redux';
import RestHelper from 'utils/RestHelper';
import {
  isAndroid,
  isAndroidOrHarmony,
  isIos,
  isMac,
  isMobile,
  isPc,
  isPcOrLinux,
  isPlaystation,
  isSwitch,
  PlatformUtil,
} from 'utils/platform';
import { convertIosModelToProductName } from 'utils/model-convert/ios-model-convert';
import { decodeCsSdkEncodedValue, getIssueDetailUrlPrefix } from 'utils/helper';
import { FieldName, QueryType } from 'components/commons/IssueCrashFilter/IssueCrashFilterExUtil';
import { VmTypeInt } from 'utils/constants/vm-type';
import { IssueAggregateType } from 'utils/constants/issue-aggregate-type';
import { EnvUtil } from 'utils/env-util';
import { useTranslation } from 'react-i18next';
import { ze } from 'utils/zhEn';
import { useGetState } from 'ahooks';
import { isNotNullish } from 'utils/nullish';
import { actionQuerySavedIssueAiReason, getCrashAttachment, getCrashDoc } from 'reducers/issue/issueActions';
import useZuStore from 'store/zustand/zustand-store';
import scss from './AiDrawer.scss';
import AiReasoningStatusBlock from 'components/top/AiDrawer/AiReasoningStatusBlock';
import AiChatButtons from 'components/top/AiDrawer/AiChatButtons';
import { ClipboardUtil } from 'utils/clipboard-util';
import { AiDrawerHooks } from 'components/top/AiDrawer/ai-drawer-hooks';
import AiDrawerAutoScrollBody from 'components/top/AiDrawer/AiDrawerAutoScrollBody';
import AiChatBotBubble from 'components/top/AiDrawer/AiChatBotBubble';
import { DataDetailUtil } from 'components/exception/issue/DataDetail';
import { getAllInOneReduxState } from 'index';
import AiIssueAnalysisAnswerRenderer from 'components/top/AiDrawer/AiIssueAnalysisAnswerRenderer';
import { Base64 } from 'utils/base64';
import AiAnalysisFeedbackBlock from 'components/top/AiDrawer/AiAnalysisFeedbackBlock';
import { CS_STYLES } from 'utils/constants/style-constants';
import { IssueAiSolutionItemType } from 'components/exception/issue/IssueHead/IssueAiSolution';
import { sleep } from 'utils/sleep';
import { ExceptionCategoryUtil } from 'utils/exception-category';
import { Link } from 'react-router-dom';
import { CrashModuleUtil } from 'utils/crash-module-util';
import AiIssueAnalysisAttachmentSelect, {
  AiIssueAnalysisAttachmentBackendType,
  AiIssueAnalysisAttachmentType
} from 'components/top/AiDrawer/AiIssueAnalysisAttachmentSelect';
import AiIssueAnalysisFileUpload from 'components/top/AiDrawer/AiIssueAnalysisFileUpload';
import AiIssueAnalysisAddCode from 'components/top/AiDrawer/AiIssueAnalysisAddCode';
import { ModuleListUtil } from 'components/exception/issue/ModuleList/ModuleList';
import { RegisterInfoUtil } from 'components/exception/issue/RegisterInfo/RegisterInfo';
import AiIssueAnalysisQueryRenderer from 'components/top/AiDrawer/AiIssueAnalysisQueryRenderer';
import IssueCrashFilterEx from 'components/commons/IssueCrashFilter/IssueCrashFilterEx';

// 移除外部的customContentTextRenderer函数定义，改为内联使用

const AiIssueAnalysisDrawerBody = (props) => {
  const {
    reduxState,
    issueId,
    trigger,
    embedInChatDrawer = false,
    embedConversationId = '',
    forceNewAnalysis = false,
    overrideAppId,
    overridePid,
    bottomBarLeftElement,
  } = props;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const aiDrawerSlice = useZuStore().aiDrawer;

  const currentApp = reduxState.app.get('current').toJS();
  const appId = overrideAppId || currentApp.appId;
  const pid = overridePid || currentApp.pid;

  const issueIdToAiReason = reduxState.issue.get('issueIdToAiReason') || {};
  const currentIssueAiReason = issueIdToAiReason[issueId] || {};
  const hasCalculatedReason = currentIssueAiReason.exists;
  const calculatedLabels = currentIssueAiReason?.labels || [];

  const currentIssueObj = useMemo(() => reduxState.issue.get('current').toJS(), [reduxState.issue.get('current')]) || {};
  const currentCrashId = currentIssueObj?.crashDoc?.crashMap?.crashId;

  const { issueExceptionType } = currentIssueObj;
  const exceptionCategory = ExceptionCategoryUtil.fromExceptionTypeInt(issueExceptionType);

  const [forceScrollToBottomTrigger, setForceScrollToBottomTrigger, getForceScrollToBottomTrigger] = useGetState(0);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [scrollByViewMoreTypeTrigger, setScrollByViewMoreTypeTrigger, getScrollByViewMoreTypeTrigger] = useGetState(0);

  const drawerBodyRef = useRef(null);

  const [queryTriggered, setQueryTriggered] = useState(false);

  const [aiResultId, setAiResultId] = useState('');

  const [conversationId, setConversationId, getConversationId] = useGetState('');

  const [queryCodeInfoList, setQueryCodeInfoList] = useState([]);
  const [queryDescText, setQueryDescText] = useState('');
  const [selectedAttachmentList, setSelectedAttachmentList] = useState([]);
  const [uploadedFileList, setUploadedFileList] = useState([]);

  const [currentAnswerInfo, setCurrentAnswerInfo, getCurrentAnswerInfo] = useGetState(null);
  const [isAnswerOutputting, setIsAnswerOutputting, getIsAnswerOutputting] = useGetState(false);
  const [chatHistoryList, setChatHistoryList, getChatHistoryList] = useGetState([]);

  const finalChatHistoryList = useMemo(() => {
    return [...chatHistoryList, currentAnswerInfo]
      .filter(x => x);
  }, [chatHistoryList, currentAnswerInfo]);

  const [abortController, setAbortController] = useState(null);
  // 使用 ref 保存 abortController 的引用，确保在组件卸载时能拿到最新的值
  const abortControllerRef = useRef(null);

  const { isLike, isDislike, onClickLikeOrDislike } = AiDrawerHooks.useLikeDislike(appId, aiResultId);

  // 同步 abortController 到 ref
  useEffect(() => {
    abortControllerRef.current = abortController;
  }, [abortController]);

  // 组件卸载时中断请求（切换到不同问题或不同内容类型时）
  useEffect(() => {
    return () => {
      // 使用 ref 获取最新的 abortController 并中断
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const isFreeStorageAvailable = isAndroid(pid) || isIos(pid);
  const isFreeMemAvailable = isMobile(pid);
  const isFreeProcessMemAvailable = isIos(pid, appId);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    startAnalysis();
  }, [trigger]);

  useEffect(() => {
    if (!scrollByViewMoreTypeTrigger) {
      return;
    }
    scrollByViewMoreType();
  }, [scrollByViewMoreTypeTrigger]);

  async function scrollByViewMoreType() {
    // 因为AiDrawerAutoScrollBody里面自动滚动到底部的时候有sleep 100的逻辑，这个必须比他执行的晚，不然不生效
    await sleep(200);
    let findText = '';
    switch (aiDrawerSlice.issueAnalysisViewMoreType) {
      case IssueAiSolutionItemType.ROOT_CAUSE: {
        findText = ze('根因结论', 'Root Cause Conclusion');
        break;
      }
      case IssueAiSolutionItemType.SUGGESTION: {
        findText = ze('修复建议', 'Fix Recommendations');
        break;
      }
    }
    const drawerBodyDom = drawerBodyRef?.current;
    if (drawerBodyDom && findText) {
      // 在drawerBodyDom下面，找到最后一个包含findText的dom，然后滚动到这个dom
      const elements = drawerBodyDom.querySelectorAll('*');
      let targetElement = null;
      for (let i = elements.length - 1; i >= 0; i--) {
        if (elements[i].textContent.includes(findText)) {
          targetElement = elements[i];
          break;
        }
      }
      if (targetElement) {
        targetElement.scrollIntoView();
      }
    }
  }

  // TODO: 下面和DeviceStats有很多重复逻辑
  function makeFieldFilterGt0(field) {
    return {
      conditions: [{
        field,
        queryType: QueryType.RANGE,
        gt: 0,
      }],
    };
  }

  const fields = [{
    available: isMobile(pid),
    name: 'hardware',
    label: t('hardwareInfo.机型设备'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: convertIosModelToProductName(decodeCsSdkEncodedValue(x.key)),
        count: x.docCount,
      }))
    },
  }, {
    available: true,
    name: isPcOrLinux(pid) ? 'osName' : 'os',
    label: t('hardwareInfo.系统版本'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: decodeCsSdkEncodedValue(x.key),
        count: x.docCount,
      }))
    },
  }, {
    available: isAndroid(pid),
    name: 'brand',
    label: t('hardwareInfo.厂商'),
    aggregateType: 0,
  }, {
    available: isAndroid(pid),
    name: 'cpuType',
    label: t('REPORTDETAIL.cpu'),
    aggregateType: 0,
  }, {
    available: isMac(pid) || isPc(pid, appId),
    name: 'cpu',
    label: t('issueCrashFilterKey.cpu'),
    aggregateType: 0,
  }, {
    available: isAndroidOrHarmony(pid),
    name: 'appBit',
    label: t('issueCrashFilterKey.appBit'),
    aggregateType: 0,
    bucketKeyDict: {
      0: ze('未知', 'Unknown'),
      32: ze('32位', '32 bit'),
      64: ze('64位', '64 bit'),
    },
  }, {
    available: isPc(pid, appId),
    name: 'displayVersion',
    label: t('issueCrashFilterKey.gpuDriverVersion'),
    aggregateType: 0,
  }, {
    available: isMobile(pid),
    name: 'ram',
    label: t('REPORTDETAIL.memSize'),
    aggregateType: 0,
    bucketListHandler: (o) => {
      return o.bucketList.map(x => ({
        ...x,
        name: (!x.isShowAsOthers && !x.isNoData) ? `${Number(x.key)} GiB` : x.key,
        count: x.docCount,
      }));
    },
  }, {
    available: isAndroidOrHarmony(pid),
    name: 'isRooted',
    label: t('hardwareInfo.是否ROOT'),
    aggregateType: 0,
    bucketKeyDict: {
      false: ze('否', 'No'),
      true: ze('是', 'Yes'),
    },
  }, {
    available: isMobile(pid),
    name: 'crashDataType',
    label: t('hardwareInfo.是否是系统退出'),
    aggregateType: 0,
    bucketKeyDict: {
      false: ze('否', 'No'),
      true: ze('是', 'Yes'),
    },
  }, {
    available: isAndroidOrHarmony(pid),
    name: FieldName.vmType,
    label: t('issueCrashFilterKey.vmType'),
    aggregateType: 0,
    bucketKeyDict: {
      [VmTypeInt.ALL]: ze('未知', 'Unknown'),
      [VmTypeInt.REAL_DEVICE]: t(`vmTypeOptions.${VmTypeInt.REAL_DEVICE}`),
      [VmTypeInt.EMULATOR]: t(`vmTypeOptions.${VmTypeInt.EMULATOR}`),
      [VmTypeInt.CLOUD_GAMING]: t(`vmTypeOptions.${VmTypeInt.CLOUD_GAMING}`),
    },
  }, {
    available: isAndroid(pid),
    name: FieldName.vmDeviceBrand,
    label: t('issueCrashFilterKey.vmDeviceBrand'),
    aggregateType: 0,
  }, {
    available: isFreeStorageAvailable,
    name: 'freeStorageR',
    label: t('hardwareInfo.存储可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeStorage'),
  }, {
    available: isFreeProcessMemAvailable,
    name: 'freeProcessMemR',
    label: t('hardwareInfo.进程内存可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeMem'),
  }, {
    available: isFreeMemAvailable,
    name: 'freeMemR',
    label: t('hardwareInfo.内存可用占比'),
    aggregateType: IssueAggregateType.HISTOGRAM,
    histogramInterval: 0.01,
    fieldFilter: makeFieldFilterGt0('freeMem'),
  }].filter(x => x.available).map(x => ({
    ...x,
    termsSizeLimit: 300,
    isSelfDefinedKeyValue: false,
  }));

  async function queryAggAndMakeDistributionParam(searchConditionGroup) {
    const defaultScg = {
      conditions: [
        { field: FieldName.crashUploadTime, queryType: QueryType.RANGE_RELATIVE_DATETIME, gte: 7 * 86400 * 1000 },
      ],
    };
    const rsp = await RestHelper.post('/api/issue/queryIssueAggregate', {
      appId,
      issueHash: issueId,
      customFields: fields,
      searchConditionGroup: searchConditionGroup || defaultScg,
    });
    const entries = rsp.json.data
      .map((x) => {
        const { name, bucketList } = x;
        return [name, bucketList];
      });
    const distributionParam = Object.fromEntries(entries);
    return distributionParam;
  }

  /**
   * 调用接口，让后台上传附件。返回difyFileIdList
   * @return {{difyFileId: string, fileName: string}[]}
   * */
  async function prepareAndUploadAttachments() {
    if (!currentCrashId || selectedAttachmentList.length === 0) {
      return;
    }
    let params = {};
    if (selectedAttachmentList.includes(AiIssueAnalysisAttachmentType.MODULES)) { // 获取加载模块信息
      const modulesTableData = ModuleListUtil.getModuleListTableData(currentIssueObj, pid);
      params.moduleInfoBase64 = Base64.encodeAsString(JSON.stringify(modulesTableData));
    }
    if (selectedAttachmentList.includes(AiIssueAnalysisAttachmentType.REGISTERS)) { // 获取寄存器信息
      const registersParseResult = RegisterInfoUtil.getParseResult(currentIssueObj, pid);
      params.registerBase64 = Base64.encodeAsString(JSON.stringify(registersParseResult));
    }
    const realAttachmentList = selectedAttachmentList.filter(x => x !== AiIssueAnalysisAttachmentType.MODULES && x !== AiIssueAnalysisAttachmentType.REGISTERS && x !== AiIssueAnalysisAttachmentType.ATTACHMENTS);
    params.attachments = realAttachmentList.map(x => ({ fileName: x }));
    const rsp = await RestHelper.mustPost('/api/aiPilot/uploadAttachment', {
      appId,
      issueId,
      crashId: currentCrashId,
      conversationId: getConversationId(),
      ...params,
    });
    const difyFileInfoList = rsp.json.data;
    return difyFileInfoList;
  }

  // 查询当前上报哪些模块没有成功还原。如果还原失败了要增加提示显示给用户看
  function getRetraceFailedSoNames() {
    // 这里强制拿最新的reduxState
    const currentIssueObj = getAllInOneReduxState().issue.get('current').toJS() || {};
    const {
      crashDoc,
    } = currentIssueObj;
    const { esMap, crashMap } = crashDoc || {};
    const {
      retraceResultInfo,
      appSoNameList,
    } = esMap || {};
    const {
      crashId,
      systemSoNameList,
    } = crashMap || {};

    // 如果没有crash ID，说明可能是在更多上报界面，这种情况下不展示提示
    if (!crashId) {
      return [];
    }

    const lowerSystemSoNameList = (systemSoNameList || []).map(x => (x || '').toLowerCase());
    const lowerAppSoNameList = (appSoNameList || []).map(x => (x || '').toLowerCase());
    const failedSoNames = Object.entries(retraceResultInfo || {})
      .filter(([k, v]) => {
        return !CrashModuleUtil.getIsSystemModule(k, lowerSystemSoNameList, lowerAppSoNameList)
          && v
          && !CrashModuleUtil.getIsCrashSightModule(k); // CrashSight自己的模块不提示
      })
      .map(([k, v]) => k);
    return failedSoNames;
  }

  function onEventSourceMessage(rawData) {
    let eventObject;
    try {
      eventObject = JSON.parse(rawData);
    } catch (e) {
      console.error(`explainAsmCodeByAi error when parsing: `, rawData);
      return;
    }

    const answerInfo = {...(getCurrentAnswerInfo() || {
      isBotNotUser: true,
      text: '',
      isLoading: true,
    })};

    const {
      event,
      id,
      conversationId: messageConversationId,
      taskId,
      messageId,
      answer,
    } = eventObject;

    if (messageConversationId) {
      setConversationId(messageConversationId);
    }

    if (event === 'message' || event === 'agent_message') {
      answerInfo.text += answer || '';
      answerInfo.messageId = messageId;
      answerInfo.isLoading = false;
      answerInfo.isOutputting = true;
      setCurrentAnswerInfo(answerInfo);
    } else if (event === 'QuotedLogs' && answer) {
      const quotedLogs = answer;
      answerInfo.otherInfo = {
        ...answerInfo.otherInfo,
        quotedLogs,
      };
      setCurrentAnswerInfo(answerInfo);
    } else if (event === 'AiAnalyseResult') { // AiAnalyseResult是最后一条消息
      setAiResultId(answer || '');
      answerInfo.isOutputting = false;
      setIsAnswerOutputting(false);
      setChatHistoryList([...getChatHistoryList(), answerInfo]);
      setCurrentAnswerInfo(null);
    }
  }

  function makeOtherQueryIssueReasonParams() {
    // 这里强制拿最新的reduxState
    const currentIssueObj = getAllInOneReduxState().issue.get('current').toJS() || {};
    const {
      esCount: crashNums,
      esDeviceCount: crashDevices,
      crashDoc,
      attachList,
      isShowingMinidumpStack,
      pcRegisterInfoLines,
      pcModuleInfoList,
    } = currentIssueObj;
    const { esMap, crashMap } = crashDoc || {};
    const {
      modelBit,
    } = esMap || {};
    const {
      expName,
      threadName,
      processName,
    } = crashMap || {};

    let registerInfoLines = [];
    if (!isShowingMinidumpStack && (isAndroidOrHarmony(pid) || isSwitch(pid))) {
      registerInfoLines = DataDetailUtil.makeRegisterInfoTextLines(pid, attachList, modelBit);
    } else if (!isShowingMinidumpStack && PlatformUtil.isIosOrMac(pid)) {
      registerInfoLines = DataDetailUtil.makeRegisterInfoTextLines(pid, attachList, modelBit);
    } else if (isShowingMinidumpStack || isPcOrLinux(pid) || isPlaystation(pid)) {
      registerInfoLines = pcRegisterInfoLines || [];
    }

    let moduleInfoLines = isPcOrLinux(pid) || isShowingMinidumpStack
      ? (pcModuleInfoList || []).map(x => `${x.moduleName}  ${x.uuid}`)
      : DataDetailUtil.makeSoInfoTextLines(pid, attachList);

    return {
      registerInfo: (registerInfoLines || []).join('\n'),
      expProcess: processName,
      expThread: threadName,
      expName,
      moduleInfo: (moduleInfoLines || []).join('\n'),
      crashNums,
      crashDevices,
    };
  }

  async function queryIssueReason({ newConversation, customQueryText, searchConditionGroup }) {
    let queryText = '';
    if (customQueryText) {
      queryText = customQueryText;
    } else {
      const hasCodeInfo = queryCodeInfoList.length > 0 && queryCodeInfoList.some(item => item.line && item.codeText);
      const trimmedDescText = queryDescText.trim();
      if (hasCodeInfo || trimmedDescText || selectedAttachmentList.length > 0 || uploadedFileList.length > 0) {
        queryText = ze('请基于以下补充信息，进行深入分析：\n\n', 'Please conduct in-depth analysis based on the following additional information:\n\n');
        if (hasCodeInfo) {
          queryText += ze('补充的代码片段：  \n', 'Additional code snippets:  \n');
          const validQueryCodeInfoList = queryCodeInfoList.filter(item => item.line && item.codeText);
          validQueryCodeInfoList.forEach((codeInfo, index) => {
            queryText += `${index + 1}. ${ze('堆栈行：', 'Stack line:')}\`\`\`${codeInfo.line}\`\`\`  \n`;
            queryText += `${ze('对应代码：', 'Corresponding code:')}\n\`\`\`\n${codeInfo.codeText}\n\`\`\`  \n`;
          });
        }
        if (trimmedDescText) {
          queryText += `${ze('补充的上下文信息：', 'Additional context information:')}  \n\`\`\`\n${queryDescText}\n\`\`\`\n\n`;
        }
      }
    }
    if (!newConversation && !(queryText.trim())) {
      message.error(ze('请输入内容', 'Please input content'));
      return;
    }

    // 用户额外上传的文件
    let userUploadedFiles = [];
    if (!newConversation && uploadedFileList.length > 0) {
      userUploadedFiles = uploadedFileList.map(x => {
        const { response, name } = x;
        return {
          difyFileId: response?.id,
          fileName: name,
        }
      });
      if (userUploadedFiles.some(x => !x.difyFileId)) {
        message.error(ze('存在上传失败的文件，请先移除后再提交', 'Some files failed to upload. Please remove them and try again'));
        return;
      }
    }

    setQueryTriggered(true);
    if (newConversation) {
      setSelectedAttachmentList([]);
      setChatHistoryList([]);
      setConversationId('');
    }
    setAiResultId('');
    setCurrentAnswerInfo({
      isBotNotUser: true,
      text: '',
      isLoading: true,
      otherInfo: !!searchConditionGroup ? {
        searchConditionGroup,
      } : undefined,
    });
    setIsAnswerOutputting(true);

    const distribution = await queryAggAndMakeDistributionParam(searchConditionGroup);

    // 在聊天历史追加用户气泡的条件：
    // 1. 非新会话 + queryText 存在（原有逻辑，补充分析场景）
    // 2. 新会话 + customQueryText 存在（从顶部 AI 助手"关联问题"入口跳转过来、用户带了自定义首轮 prompt）
    //    此时 chatHistoryList 已被上面的 newConversation 分支清空，所以追加的就是该 prompt 对应的用户气泡
    const shouldAppendUserBubble = queryText && (!newConversation || !!customQueryText);
    if (shouldAppendUserBubble) {
      const newChatHistoryList = [
        ...getChatHistoryList(),
        {
          isBotNotUser: false,
          text: queryText,
          otherInfo: {
            attachments: selectedAttachmentList.map(x => {
              let fileName;
              if (x === AiIssueAnalysisAttachmentType.REGISTERS) {
                fileName = AiIssueAnalysisAttachmentBackendType.register;
              } else if (x === AiIssueAnalysisAttachmentType.MODULES) {
                fileName = AiIssueAnalysisAttachmentBackendType.module;
              } else {
                fileName = x;
              }
              return { fileName };
            }),
            userUploadedFiles: userUploadedFiles.map(x => {
              const { fileName } = x;
              return { fileName };
            }),
          },
        }
      ];
      setChatHistoryList(newChatHistoryList);
    }

    // 需要额外上传选中的附件等信息
    let difyFileInfoList = [];
    if (!newConversation && selectedAttachmentList.length > 0) {
      difyFileInfoList = await prepareAndUploadAttachments();
    }

    let conversationParams = {
      conversationId: newConversation ? undefined : getConversationId(),
      queryBase64: Base64.encodeAsString(queryText),
      attachments: difyFileInfoList,
      userUploadedFiles,
    };

    setQueryCodeInfoList([]);
    setQueryDescText('');
    setSelectedAttachmentList([]);
    setUploadedFileList([]);

    const currentIssueObj = reduxState.issue.get('current').toJS() || {};
    const { crashDoc } = currentIssueObj;
    const { crashMap } = crashDoc || {};
    if (!crashMap?.crashId) {
      const { crashIdList } = reduxState.crash.toJS();
      if (crashIdList?.length > 0) {
        const crashId = crashIdList[0];
        const tasks = [dispatch(getCrashDoc(issueId, crashId)), dispatch(getCrashAttachment(issueId, crashId))];
        await Promise.all(tasks);
      }
      // crashIdList 为空时（如从问题分析工作区弹窗输入 issueId 新建分析），
      // 跳过本地 crash 数据获取，后端 attributeIssueReason 接口会自行查询
    }

    const params = {
      appId,
      issueId,
      clearCache: true,
      distribution,
      searchConditionGroup,
      ...makeOtherQueryIssueReasonParams(),
      ...conversationParams,
    };

    if (isNotNullish(abortController)) {
      abortController.abort();
      setAbortController(null);
    }
    const ac = new AbortController();
    setAbortController(ac);
    const url = '/api/aiPilot/attributeIssueReason';
    const options = {
      signal: ac.signal,
      statusCodeToCallback: {
        500() {
          message.error(ze('用户后台正忙请稍后再试','The user background is busy, please try again later'));
        },
      },
      onmessage: (event) => {
        const rawData = event.data;
        if (!rawData) {
          return;
        }
        onEventSourceMessage(rawData);
      },
    };
    await RestHelper.postEventStream(url, params, options);
  }

  /** 基于假设深度分析 */
  async function onClickDeepAnalysis(text) {
    setForceScrollToBottomTrigger(getForceScrollToBottomTrigger() + 1);
    await queryIssueReason({
      newConversation: false,
      customQueryText: text,
    });
  }

  async function queryChatHistory(conversationId) {
    const rsp = await RestHelper.mustPost('/api/aiPilot/getChatHistory', {
      appId,
      issueId,
      conversationId: conversationId || undefined,
    });
    const jsonData = rsp.json.data;
    if (!jsonData || (jsonData.data || []).length === 0) {
      setChatHistoryList([]);
      setAiResultId('');
      setConversationId('');
      return;
    }

    const {
      data,
      aiResultId,
    } = jsonData;

    const chatHistoryList = data
      .map(x => {
        const {
          id: messageId,
          conversationId,
          query,
          answer,
          feedback,
          localUserId,
          userName,
          requestTime,
          responseTime,
          otherInfo,
          jsonFormat,
          systemGenerated,
        } = x;

        // otherInfo?.searchConditionGroup是这次对话使用的过滤条件，仅对满足过滤条件的上报做分析。
        // 后台执行的时候会往searchConditionGroup里面添加issue ID来搜索。界面上显示的时候，需要移除issue ID字段。
        if (Array.isArray(otherInfo?.searchConditionGroup?.conditions)) {
          otherInfo.searchConditionGroup.conditions = otherInfo.searchConditionGroup.conditions.filter(x => x.field !== FieldName.issueId);
        }

        return [{
          messageId,
          conversationId,
          isBotNotUser: false,
          text: query,
          localUserId,
          userName,
          requestTime,
          responseTime,
          otherInfo,
        }, {
          messageId,
          conversationId,
          feedback,
          isBotNotUser: true,
          text: answer,
          localUserId,
          userName,
          requestTime,
          responseTime,
          otherInfo,
          jsonFormat,
          systemGenerated,
        }];
      })
      .flat()
      .filter(x => x.text);

    setChatHistoryList(chatHistoryList);
    setAiResultId(aiResultId || '');
    if (chatHistoryList.length > 0) {
      setConversationId(chatHistoryList[0].conversationId);
    } else {
      setConversationId('');
    }
  }

  async function onClickFeedback(messageId, feedback) {
    await RestHelper.mustPost('/redir/api/aiResult/updateAiFeedback', {
      appId,
      messageId,
      conversationId: getConversationId(),
      feedback,
    });

    if (getCurrentAnswerInfo()?.messageId === messageId) {
      setCurrentAnswerInfo({
        ...currentAnswerInfo,
        feedback,
      });
    } else {
      const newChatHistoryList = getChatHistoryList().map(x => {
        if (x.messageId === messageId) {
          return {
            ...x,
            feedback,
          };
        }
        return x;
      });
      setChatHistoryList(newChatHistoryList);
    }
  }

  async function startAnalysis() {
    setDrawerLoading(true);
    const historyConversationId = embedInChatDrawer
      ? embedConversationId
      : aiDrawerSlice.issueAnalysisConversationId;
    const forceNewConversation = embedInChatDrawer
      ? forceNewAnalysis
      : aiDrawerSlice.issueAnalysisForceNewConversation;

    if (!forceNewConversation) {
      await queryChatHistory(historyConversationId);
    } else {
      setChatHistoryList([]);
      setAiResultId('');
      setConversationId('');
    }
    setDrawerLoading(false);
    if (!getConversationId()) {
      if (embedInChatDrawer && !forceNewConversation) {
        return;
      }
      await queryIssueReason({
        newConversation: true,
        searchConditionGroup: aiDrawerSlice.issueAnalysisSearchConditionGroup,
        customQueryText: aiDrawerSlice.issueAnalysisInitialQueryText || '',
      });
    } else if (aiDrawerSlice.issueAnalysisViewMoreType) {
      requestAnimationFrame(() => {
        setScrollByViewMoreTypeTrigger(scrollByViewMoreTypeTrigger + 1);
      });
    }
  }

  const retraceFailedSoNames = getRetraceFailedSoNames();
  const retraceFailedAlert = retraceFailedSoNames.length > 0
    && <div>
      <Alert
        message={ ze('当前上报存在部分模块未还原，AI分析结果可能不准确。', 'Some modules of current report was not successfully symbolicated, and the AI analysis results may be inaccurate.') }
        description={ ze(`未还原的模块：${retraceFailedSoNames.join(', ')}`, `Unsymbolicated modules: ${retraceFailedSoNames.join(', ')}`) }
        type="warning"
        showIcon
      />
    </div>;

  // 当前对话使用的上报过滤条件（来自更多上报界面，点击了按搜索条件AI分析）
  let usedScgDom = null;
  if (finalChatHistoryList?.[0]?.otherInfo?.searchConditionGroup) {
    const usedScg = finalChatHistoryList[0].otherInfo.searchConditionGroup;
    usedScgDom = <div className={scss.chatBubbleContainer}>
      <div>{ze('当前分析结论来自于满足以下搜索条件的上报：', 'Current analysis conclusions are based on reports that meet the following search criteria:')}</div>
      <div>
        <IssueCrashFilterEx
          isReadOnly={true}
          platformId={pid}
          searchConditionGroup={usedScg}
        />
      </div>
    </div>;
  }

  const bubbleDomList = finalChatHistoryList
    .map((x, i) => {
      const systemGenerated = !!x.systemGenerated;
      const messageTime = !!x.isBotNotUser ? x.responseTime : x.requestTime;
      const messageTimeText = !!x.isBotNotUser
        ? `${x.userName} ${messageTime} ${ze('创建', 'Created')} ${systemGenerated ? ze('（离线分析）', ' (Offline Analysis)') : ''}`
        : `${x.userName} ${messageTime}`

      const crashId = x.otherInfo?.crashId;
      let relatedCrashUrl;
      if (crashId) {
        relatedCrashUrl = `/${getIssueDetailUrlPrefix(exceptionCategory)}/${appId}/${issueId}/report-id/${crashId}?pid=${pid}`;
      }

      const enableDeepAnalysis = !systemGenerated && !getIsAnswerOutputting();

      return <AiChatBotBubble
        key={i}
        messageId={x.messageId}
        conversationId={getConversationId()}
        isBotNotUser={!!x.isBotNotUser}
        customContentTextRenderer={(text) => {
          if (x.isBotNotUser) {
            return <div className={scss.aiMarkdown}>
              <AiIssueAnalysisAnswerRenderer
                text={text}
                otherInfo={x.otherInfo}
                enableDeepAnalysis={enableDeepAnalysis}
                onClickDeepAnalysis={(text) => onClickDeepAnalysis(text)}
              />
            </div>;
          } else {
            if (x.otherInfo?.attachments?.length > 0) {
            text = text + `\n\n${ze('补充的附件信息：', 'Additional attachment information:')}  \n`;
              x.otherInfo?.attachments.forEach(attach => {
                let label;
                const { fileName } = attach || {};
                if (fileName === AiIssueAnalysisAttachmentBackendType.register) {
                  label = t('LASTREPORT.崩溃线程寄存器信息');
                } else if (fileName === AiIssueAnalysisAttachmentBackendType.module) {
                  label = t('LASTREPORT.加载模块信息');
                } else {
                  label = fileName;
                }
                text = text + `${label}  \n`;
              });
            }

            if (x.otherInfo?.userUploadedFiles?.length > 0) {
              text = text + `\n\n${ze('上传的文件：', 'Uploaded files:')}  \n`;
              x.otherInfo.userUploadedFiles.forEach(x => {
                const { fileName } = x || {};
                text = text + `${fileName}  \n`;
              });
            }

            return <div className={scss.aiMarkdown}>
              <AiIssueAnalysisQueryRenderer
                text={text}
              />
            </div>
          }
        }}
        text={x.text}
        isLoading={!!x.isLoading}
        messageTimeInfo={x.userName && messageTime && <div style={{ color: CS_STYLES.SUB_TEXT_COLOR, fontSize: '12px' }}>{ messageTimeText }</div>}
        childElement={<div>
          { x.isBotNotUser && crashId && <div style={{ marginTop: '12px', padding: '8px 12px', border: '1px solid #CCC', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px' }}>
              <div>
                <span>{ ze('本条分析结论由以下上报数据得出：', 'This analysis conclusion is derived from the following report data:') }</span>
              </div>
              <div>
                <span>Crash ID：#{crashId} </span>
                <Link to={relatedCrashUrl} target='_blank' rel='noopener noreferrer'>{ ze('查看上报', 'View Detail')}</Link>
              </div>
            </div>
          </div> }
          { x.isBotNotUser && x.messageId && !x.isLoading && !x.isOutputting && <div>
            <Divider/>
            <AiAnalysisFeedbackBlock
              feedbackValue={x.feedback}
              onClickFeedback={(v) => onClickFeedback(x.messageId, v)}
            />
          </div> }
        </div>}
      />;
    });

  const MAX_DIFY_FILES = 10;
  const totalSelectedDifyFiles = selectedAttachmentList.length + uploadedFileList.length;
  const isTooManyFiles = totalSelectedDifyFiles > MAX_DIFY_FILES;
  // 达到上限也要给出提示（此时「选择文件」按钮已置灰），避免用户不知道为什么点不动。
  // 提示挂在这个条件而不是「上传失败后剔除文件」，所以不会再因为单次上传失败而消失。
  const isReachLimit = totalSelectedDifyFiles >= MAX_DIFY_FILES;
  const hasValidCodeInfo = queryCodeInfoList.length > 0 && queryCodeInfoList.some(item => item.line && item.codeText);
  // 上传中 / 上传失败必须分开：antd 在 customRequest 开始上传时就把 status 置为 'uploading'，
  // 这是正常中间态，不能套用「上传失败」的红色文案（曾导致只传一个文件也报「存在上传失败」）。
  // 用白名单而不是 status !== 'done'，避免无 status 的异常条目把按钮永久置灰。
  const hasUploadingFile = uploadedFileList.some(x => x.status === 'uploading');
  const hasFailedUpload = uploadedFileList.some(x => x.status === 'error');
  const isAdditionalAnalysisDisabled = (!hasValidCodeInfo && !queryDescText && selectedAttachmentList.length === 0 && uploadedFileList.length === 0)
    || isTooManyFiles
    || hasUploadingFile
    || hasFailedUpload;

  const queryBubble = !isAnswerOutputting && <AiChatBotBubble
    isBotNotUser={true}
    childElement={<div>
      <div>
        {ze('如果您提供相关上下文，CrashSight会生成更准确的崩溃数据分析和建议。', 'If you provide more context, CrashSight will generate more accurate crash data analysis and suggestions.')}
      </div>
      {/* 补充代码区域 */}
      { currentCrashId && <AiIssueAnalysisAddCode
        popupContainer={drawerBodyRef?.current}
        codeInfoList={queryCodeInfoList}
        onCodeInfoListChange={setQueryCodeInfoList}
      /> }
      <div style={{ marginTop: '12px' }}>{ ze('分享任何上下文或相关详情：', 'Share any context or relevant details:') }</div>
      <div><Input.TextArea
        value={queryDescText}
        onChange={e => setQueryDescText(e.target.value)}
      /></div>
      { currentCrashId && <div style={{ marginTop: '12px' }}>{ ze('添加上报附件：', 'Add report attachments:') }</div> }
      { currentCrashId && <AiIssueAnalysisAttachmentSelect
        popupContainer={drawerBodyRef?.current}
        value={selectedAttachmentList}
        onChange={setSelectedAttachmentList}
      /> }
      <div style={{ marginTop: '12px' }}>{ ze('上传额外文件：', 'Upload files:') }</div>
      <AiIssueAnalysisFileUpload
        fileList={uploadedFileList}
        onFileListChange={setUploadedFileList}
        appId={appId}
        issueId={issueId}
        maxFiles={MAX_DIFY_FILES}
        usedCount={selectedAttachmentList.length}
      />
      { isReachLimit && <div style={{ color: isTooManyFiles ? CS_STYLES.PRIMARY_COLOR_RED : CS_STYLES.SUB_TEXT_COLOR, marginTop: '12px' }}>{ ze(`最多只能选择${MAX_DIFY_FILES}个文件（含上报附件）`, `You can only select up to ${MAX_DIFY_FILES} files (including report attachments)`) }</div> }
      { hasUploadingFile && <div style={{ color: CS_STYLES.SUB_TEXT_COLOR, marginTop: '12px' }}>{ ze('文件上传中，请稍候…', 'Uploading, please wait…') }</div> }
      { hasFailedUpload && <div style={{ color: CS_STYLES.PRIMARY_COLOR_RED, marginTop: '12px' }}>{ ze('存在上传失败的文件，请先移除后再提交', 'Some files failed to upload. Please remove them and try again') }</div> }
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', gap: '16px' }}>
        <Button
          type='primary'
          disabled={isAdditionalAnalysisDisabled}
          onClick={() => queryIssueReason({ newConversation: false })}
        >{ ze('补充分析', 'Submit') }</Button>
        <Button
          onClick={() => queryIssueReason({ newConversation: true })}
        >{ ze('重新开始会话', 'New Conversation') }</Button>
      </div>
    </div>}
  />;

  const scrollBodyDom = (
    <AiDrawerAutoScrollBody
      forceScrollToBottomTrigger={forceScrollToBottomTrigger}
    ><Spin spinning={drawerLoading}><div
      ref={drawerBodyRef}
    >
      { usedScgDom }
      { bubbleDomList }
      { queryBubble }
    </div></Spin></AiDrawerAutoScrollBody>
  );

  if (embedInChatDrawer) {
    return <>
      { retraceFailedAlert }
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          { scrollBodyDom }
        </div>
        { bottomBarLeftElement ? (
          <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
            { bottomBarLeftElement }
          </div>
        ) : null }
      </div>
    </>;
  }

  return <>
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        { retraceFailedAlert }
        { scrollBodyDom }
      </div>
      { bottomBarLeftElement ? (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #f0f0f0', flexShrink: 0 }}>
          { bottomBarLeftElement }
        </div>
      ) : null }
    </div>
  </>;
};


const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(AiIssueAnalysisDrawerBody);
